import { AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  signal,
  SimpleChanges,
  ViewChild } from '@angular/core';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { MpAutocompleteDropdownComponent } from './mp-autocomplete-dropdown/mp-autocomplete-dropdown.component';
import { DomEventListenerService } from '../../services/dom-event-listener.service';
import { MpTooltipDirective } from '../mp-tooltip/mp-tooltip.directive';
import { DomUtils } from '../../utils/dom.utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-mp-autocomplete',
  templateUrl: './mp-autocomplete.component.html',
  imports: [
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    NgForOf,
    MpTooltipDirective,
    NgClass,
    MpAutocompleteDropdownComponent,
    NgIf
  ],
  styleUrl: './mp-autocomplete.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MpAutocompleteComponent implements AfterViewInit, OnChanges, OnDestroy {

  /** 'small' | 'medium' | 'large' */
  @Input() public size: 'small' | 'medium' | 'large' = 'large';
  @Input() public outlined: boolean = false;
  @Input() public outlinedColor: 'primary' | 'complementary' | 'accent' | 'yellow' = 'primary';
  @Input() public outlinedStyle: 'solid' | 'dashed' = 'solid';
  @Input() public customClasses: string = '';
  @Input() public faIcon: string;
  @Input() public label: string;
  @Input() public disabled: boolean;
  @Input() public isReadOnly: boolean = false;
  @Input() public inputValue: unknown;
  @Input() public overflowTooltipAllowed: boolean = false;
  @Input() public formControlField: FormControl;
  @Input() public isSameOption: (option1: unknown, option2: unknown) => boolean;
  @Input() public optionLabelBehavior: (option: unknown) => string;
  @Input() public overflowTooltipBehavior: (label: string) => string;
  @Input() public autocompleteBehavior: (option: string) => unknown[];
  @Input() public autocompleteBehaviorAsync: (option: string) => Observable<unknown[]>;

  @Output() inputValueChange = new EventEmitter<unknown>();

  @ViewChild('autocompleteField') public autocompleteFieldElementRef: ElementRef;
  @ViewChild('autocompleteOptionsRef', { static: false }) public autocompleteOptionsRef: ElementRef;
  @ViewChild('mpValueAutocomplete') public mpValueAutocomplete: ElementRef;

  @ViewChild(MpAutocompleteDropdownComponent, { static: false }) private readonly dropdownComponent: MpAutocompleteComponent;
  @ViewChild(MpAutocompleteDropdownComponent, {
    static: false,
    read: ElementRef
  }) private readonly dropdownComponentElementRef: ElementRef;

  protected autocompleteValue: string;
  protected autocompleteOptions: unknown[] = [];
  protected highlightedOptionIndex: number = undefined;

  protected isEllipsisActive: boolean = false;
  private optionClicked: boolean = false;
  private readonly _destroyRef = inject(DestroyRef);

  private scrollListenerSubscription: Subscription;
  private clickListenerSubscription: Subscription;

  protected displayDropdown = signal(false);

  private valueChangeTimer: ReturnType<typeof setTimeout>;
  private autoCompleteFocusFirstOptionTimeout: ReturnType<typeof setTimeout>;
  private autoCompleteUpdateEllipsisTimeout: ReturnType<typeof setTimeout>;

  constructor(private readonly elementRef: ElementRef,
              private readonly domListener: DomEventListenerService,
              private readonly ngZone: NgZone) {}

  get selectedValue() {
    return this.formControlField ? this.formControlField.value : this.inputValue;
  }

  set selectedValue(value: unknown) {
    if (this.formControlField) {
      this.formControlField.setValue(value);
    } else {
      this.inputValue = value;
    }
  }

  ngAfterViewInit() {
    this.setValues();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['formControlField']) {
      this.setValues();
    }

    if (changes['inputValue'] && !changes['inputValue'].previousValue) {
      this.setValues();

      if (!this.inputValue) {
        this.autocompleteValue = null;
      }
    }
  }

  ngOnDestroy() {
    this.removeDropdown();
  }

  private setValues() {
    if (this.inputValue) {
      this.autocompleteValue = this.getOptionLabel(this.inputValue);
    } else if (this.formControlField) {
      this.autocompleteValue = this.getOptionLabel(this.formControlField.value);
    }

    if (this.formControlField) {
      this.formControlField.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
        this.onValueChanged();
      });
    }

    this.updateEllipsisActive();
  }

  // If the value is larger than the component, we add a tooltip
  private applyOverflowTooltip() {
    return this.getOverflowTooltip(this.autocompleteValue);
  }

  updateEllipsisActive() {
    if (this.autoCompleteUpdateEllipsisTimeout) {
      clearTimeout(this.autoCompleteUpdateEllipsisTimeout);
    }

    this.autoCompleteUpdateEllipsisTimeout = setTimeout(() => {
      const valueElement = this.autocompleteFieldElementRef?.nativeElement?.querySelector('#mp-value-autocomplete');
      this.isEllipsisActive = DomUtils.isEllipsisActive(valueElement);
    });
  };

  public onValueChanged() {
    this.highlightedOptionIndex = 0;

    if (this.valueChangeTimer) {
      clearTimeout(this.valueChangeTimer);
    }

    if (this.optionClicked) {
      return;
    }

    if (!this.autocompleteValue) {
      this.optionSelected();
    } else {
      this.updateAvailableOption();
    }
  }

  public optionSelected(option?: unknown) {
    this.optionClicked = true;
    this.autocompleteValue = this.getOptionLabel(option);

    this.selectedValue = option;

    this.inputValueChange.emit(option);
    this.removeDropdown();

    if (this.overflowTooltipAllowed) {
      this.applyOverflowTooltip();
    }

    this.optionClicked = false;
    this.focus();
  }

  public getOptionLabel(option: unknown | unknown[]) {
    if (!option) {
      return '';
    }

    if (this.optionLabelBehavior) {
      return this.optionLabelBehavior(option);
    } else {
      return option.toString();
    }
  }

  public getOverflowTooltip(label: string) {
    if (!this.overflowTooltipAllowed) {
      return '';
    }

    return this.overflowTooltipBehavior ? this.overflowTooltipBehavior(label) : label;
  }

  private updateAvailableOption() {
    if (this.disabled) {
      return;
    }

    this.valueChangeTimer = setTimeout(() => {
      if (!this.optionClicked) {
        if (this.autocompleteBehaviorAsync) {
          this.fetchAsyncData();
        } else if (this.autocompleteBehavior) {
          this.autocompleteOptions = this.autocompleteBehavior(this.autocompleteValue);
          if (this.autocompleteOptions && this.autocompleteOptions.length) {
            this.openAutocompleteDropDown();
          }
        }
      } else {
        this.optionClicked = false;
      }
    }, 300);
  }

  private fetchAsyncData() {
    this.autocompleteBehaviorAsync(this.autocompleteValue).subscribe((response: unknown[]) => {
      this.autocompleteOptions = response;
      if (this.autocompleteOptions && this.autocompleteOptions.length) {
        this.openAutocompleteDropDown();
      }
    });
  }

  private removeDropdown(): void {
    this.displayDropdown.set(false);
    this.highlightedOptionIndex = undefined;
    this.scrollListenerSubscription?.unsubscribe();
    this.clickListenerSubscription?.unsubscribe();
  }

  private onDocumentScroll(event): void {
    if (this.dropdownComponentElementRef && !this.dropdownComponentElementRef?.nativeElement.contains(event.target as Node)) {
      this.ngZone.run(() => this.removeDropdown());
    }
  }

  private onDocumentClick(event): void {
    if (!this.elementRef?.nativeElement.contains(event.target)) {
      this.ngZone.run(() => this.removeDropdown());
    }
  }

  protected onKeydownArrowDown(event) {
    if (event.code !== 'ArrowDown') {
      return;
    }

    event.stopPropagation();
    event.preventDefault();

    this.focusDropdown();
  }

  protected onDocumentKeydown(event): void {
    if (!this.dropdownComponent) {
      return;
    }

    if (event.code === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();

      if (this.highlightedOptionIndex === 0) {
        this.focus();
        this.highlightedOptionIndex = undefined;
      } else if (this.highlightedOptionIndex || this.highlightedOptionIndex > 0) {
        this.highlightedOptionIndex = this.highlightedOptionIndex - 1;
      }
    }

    if (event.code === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();

      if (this.highlightedOptionIndex || this.highlightedOptionIndex === 0) {
        if (this.highlightedOptionIndex < this.autocompleteOptions.length - 1) {
          this.highlightedOptionIndex = this.highlightedOptionIndex + 1;
        } else {
          this.highlightedOptionIndex = this.autocompleteOptions.length - 1;
        }
      } else {
        this.highlightedOptionIndex = 0;
      }
    }

    if (event.code === 'NumpadEnter' || event.code === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.selectHighlightedOption();
      this.focus();
    }

    if (event.shiftKey && event.code === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
      this.removeDropdown();
      this.focus();
    }

    if (!event.shiftKey && event.code === 'Tab' && this.dropdownComponent) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  protected selectHighlightedOption() {
    const index = this.highlightedOptionIndex % this.autocompleteOptions.length;
    this.optionSelected(this.autocompleteOptions[index]);
  }

  public isSelectedOption(option: unknown) {
    if (this.selectedValue) {
      return this.sameOptionBehaviorDispatcher(option, this.selectedValue);
    } else {
      return false;
    }
  }

  public clear() {
    this.autocompleteValue = undefined;
    this.selectedValue = undefined;
  }

  private sameOptionBehaviorDispatcher(option1: unknown, option2: unknown) {
    if (this.isSameOption) {
      return this.isSameOption(option1, option2);
    } else if (option1['id'] && option2['id']) {
      return option1['id'] === option2['id'];
    } else {
      return option2 === option1;
    }
  }

  private openAutocompleteDropDown() {
    if (this.disabled || this.displayDropdown()) {
      return;
    }

    this.subscribeToDomEvents();

    if (!this.autocompleteOptions?.length || !!this.dropdownComponent) {
      return;
    }
    this.highlightedOptionIndex = 0;
    this.displayDropdown.set(true);
  }

  private subscribeToDomEvents(): void {
    this.scrollListenerSubscription?.unsubscribe();
    this.clickListenerSubscription?.unsubscribe();
    this.ngZone.runOutsideAngular(() => {
      this.scrollListenerSubscription = this.domListener.onScrollObservable
        .pipe(takeUntilDestroyed(this._destroyRef)
        ).subscribe({ next: (wEvent: WheelEvent) => this.onDocumentScroll(wEvent) });
      this.clickListenerSubscription = this.domListener.onClickObservable
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({ next: (mEvent: MouseEvent) => this.onDocumentClick(mEvent) });
    });
  }

  private focus() {
    this.mpValueAutocomplete?.nativeElement?.focus();
  }

  private focusDropdown() {
    const isAutocompleteDropdownFocused = document.activeElement === this.autocompleteOptionsRef?.nativeElement;
    const hasAvailableOptions = this.autocompleteOptions.length > 0;

    if (!isAutocompleteDropdownFocused && hasAvailableOptions) {

      if (this.autoCompleteFocusFirstOptionTimeout) {
        clearTimeout(this.autoCompleteFocusFirstOptionTimeout);
      }
      this.autoCompleteFocusFirstOptionTimeout = setTimeout(() => this.autocompleteOptionsRef.nativeElement.focus());
    }
  }
}
